import { DOCUMENT } from '@angular/common';
import { HttpContextToken, type HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { RuntimeConfigService } from '../config/runtime-config';
import { isApiRequest } from '../http/api-url';

const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Reads one cookie by name. The CSRF cookie is readable on purpose (double submit). */
export function readCookie(cookieString: string, name: string): string | null {
  for (const part of cookieString.split(';')) {
    const eq = part.indexOf('=');
    if (eq < 0) continue;
    if (part.slice(0, eq).trim() === name) return decodeURIComponent(part.slice(eq + 1).trim());
  }
  return null;
}

/**
 * Sends the session cookies with every call to the API. The cookies are HttpOnly, so this
 * only tells the browser to include them; JavaScript never reads a token.
 */
export const credentialsInterceptor: HttpInterceptorFn = (req, next) => {
  const { apiBaseUrl } = inject(RuntimeConfigService).config();
  return next(isApiRequest(req.url, apiBaseUrl) ? req.clone({ withCredentials: true }) : req);
};

/**
 * Double-submit CSRF: copies the readable CSRF cookie into the header the API checks, on
 * calls that change data. Reads the cookie on every request, because a refresh rotates it.
 */
export const csrfInterceptor: HttpInterceptorFn = (req, next) => {
  const { apiBaseUrl, csrfCookie, csrfHeader } = inject(RuntimeConfigService).config();
  if (!isApiRequest(req.url, apiBaseUrl) || !UNSAFE_METHODS.has(req.method)) return next(req);
  const token = readCookie(inject(DOCUMENT).cookie, csrfCookie);
  return next(token ? req.clone({ setHeaders: { [csrfHeader]: token } }) : req);
};

export const CORRELATION_HEADER = 'X-Correlation-Id';

/** Tags each API call with an id the server logs, so a support reference can find it. */
export const correlationInterceptor: HttpInterceptorFn = (req, next) => {
  const { apiBaseUrl } = inject(RuntimeConfigService).config();
  if (!isApiRequest(req.url, apiBaseUrl) || req.headers.has(CORRELATION_HEADER)) return next(req);
  return next(req.clone({ setHeaders: { [CORRELATION_HEADER]: crypto.randomUUID() } }));
};

export const IDEMPOTENCY_HEADER = 'Idempotency-Key';

/**
 * Set on a call to an endpoint the API marks idempotent (`[Idempotent]`, listed in its
 * contract). A generated client call passes it in its `context` argument:
 * `new HttpContext().set(IDEMPOTENT_WRITE, true)`. The key is optional on the server, so a
 * screen sets this only for writes where a double submit or a retry would do harm.
 */
export const IDEMPOTENT_WRITE = new HttpContextToken<boolean>(() => false);

/**
 * Adds one `Idempotency-Key` to a flagged POST. Registered outside the refresh interceptor, so
 * the replay after a refresh carries the same key and the server never runs the write twice.
 */
export const idempotencyInterceptor: HttpInterceptorFn = (req, next) => {
  const { apiBaseUrl } = inject(RuntimeConfigService).config();
  if (
    !req.context.get(IDEMPOTENT_WRITE) ||
    req.method !== 'POST' ||
    !isApiRequest(req.url, apiBaseUrl) ||
    req.headers.has(IDEMPOTENCY_HEADER)
  ) {
    return next(req);
  }
  return next(req.clone({ setHeaders: { [IDEMPOTENCY_HEADER]: crypto.randomUUID() } }));
};
